
import React, { useState, useEffect, useRef } from 'react';

// Configuration for animation frames - loading happens in background via HomePage
const FRAME_CONFIG = {
    basePath: '/assets/animasi/',
    prefix: 'ezgif-frame-',
    extension: '.webp',
    startFrame: 1,
    endFrame: 246,
    padLength: 3
};

// Generate frame URLs for background preloading
const generateFrameUrls = () => {
    const urls = [];
    for (let i = FRAME_CONFIG.startFrame; i <= FRAME_CONFIG.endFrame; i++) {
        const frameNumber = String(i).padStart(FRAME_CONFIG.padLength, '0');
        urls.push(`${FRAME_CONFIG.basePath}${FRAME_CONFIG.prefix}${frameNumber}${FRAME_CONFIG.extension}`);
    }
    return urls;
};

// Background preloader - does NOT block UI
let backgroundPreloadStarted = false;
const startBackgroundPreload = () => {
    if (backgroundPreloadStarted) return;
    backgroundPreloadStarted = true;

    const frameUrls = generateFrameUrls();

    // Use requestIdleCallback for non-blocking preload
    const preloadBatch = (startIndex) => {
        if (startIndex >= frameUrls.length) return;

        const batchSize = 5;
        const batch = frameUrls.slice(startIndex, startIndex + batchSize);

        batch.forEach(url => {
            const img = new Image();
            img.src = url;
        });

        // Schedule next batch during idle time
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => preloadBatch(startIndex + batchSize), { timeout: 100 });
        } else {
            setTimeout(() => preloadBatch(startIndex + batchSize), 50);
        }
    };

    // Start preloading after a short delay to not compete with critical resources
    setTimeout(() => preloadBatch(0), 100);
};

const NetworkIconLoader = ({ visible = false }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isFading, setIsFading] = useState(false);
    const minDuration = 1500; // 1.5 seconds for logo animation

    useEffect(() => {
        // Start background preload (non-blocking)
        startBackgroundPreload();

        document.body.style.overflow = 'hidden';

        // Timer to hide loader after minDuration
        const timer = setTimeout(() => {
            setIsFading(true);

            // Wait for fade animation to complete
            setTimeout(() => {
                setIsVisible(false);
                document.body.style.overflow = '';
            }, 500); // 500ms fade out
        }, minDuration);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = '';
        };
    }, []);

    // Handle visible prop for manual control
    useEffect(() => {
        if (visible) {
            setIsVisible(true);
            setIsFading(false);
            document.body.style.overflow = 'hidden';
        }
    }, [visible]);

    if (!isVisible && !visible) return null;

    return (
        <div
            className={`fixed inset-0 z-[100000] flex flex-col items-center justify-center bg-white transition-opacity duration-500 ease-out ${isFading ? 'opacity-0' : 'opacity-100'}`}
        >
            {/* Animasi Garis + Fill (Stroke & Fill Animation) */}
            <style>{`
                @keyframes drawLine {
                    0% { stroke-dashoffset: 1600; }
                    50% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: 0; }
                }
                @keyframes fillIn {
                    0%, 50% { fill-opacity: 0; }
                    70% { fill-opacity: 1; }
                    100% { fill-opacity: 1; }
                }
            `}</style>

            <svg
                data-name="Layer 2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 328.37 328.37"
                className="w-32 h-32 md:w-48 md:h-48"
                width="192"
                height="192"
            >
                <g data-name="Layer 1">
                    {/* Background Rect - Solid Blue */}
                    <rect width="328.37" height="328.37" rx="109.63" ry="109.63" fill="#00a4ff" />

                    {/* The Logo Paths - Group to Handle Fill Animation */}
                    <g
                        fill="white"
                        stroke="white"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ animation: 'fillIn 1.5s linear forwards' }}
                    >
                        {/* Left Part of N */}
                        <path
                            d="M54.99 237.74c5 7.37 10.76 14.26 17.14 20.49 6 5.81 13.93 9.02 22.38 9.02s16.67-3.35 22.66-9.35l28.8-28.89c1.84-1.79 1.89-4.86.05-6.7l-7.98-8.26c-.9-.9-2.08-1.46-3.4-1.46-1.32-.05-2.5.47-3.45 1.37L102.2 243c-4.25 4.25-11.19 4.3-15.53.05a110.7 110.7 0 0 1-14.35-17.14c-12.42-18.27-18.93-39.66-18.93-61.8s6.56-43.58 18.93-61.85c4.2-6.23 9.06-11.99 14.45-17.23 4.25-4.15 11.19-4.11 15.44.14L221.8 204.76a9.83 9.83 0 0 0 7.27 2.88c2.69-.14 5.1-1.27 6.85-3.26 9.44-11 11.85-30.45 11.85-40.27 0-22.05-8.78-38.86-13.22-45.09-.8-1.13-1.94-2.03-3.35-2.22-1.37-.24-2.79.19-3.87 1.09l-8.36 8.5c-1.7 1.46-2.17 3.87-1.09 5.85.71 1.32 4.25 6.94 6.61 15.49 1.27 4.67 2.22 10.2 2.12 16.38-.05 7.27-1.42 13.46-1.61 14.16L117.03 70.3c-6.04-6.04-14.07-9.35-22.66-9.35s-16.38 3.21-22.33 9.02C65.67 76.2 59.86 83.09 54.9 90.41c-14.78 21.76-22.57 47.26-22.57 73.65s7.79 51.89 22.57 73.6l.09.09Z"
                            style={{
                                strokeDasharray: 1600,
                                strokeDashoffset: 1600,
                                animation: 'drawLine 1.5s ease-in-out forwards'
                            }}
                        />
                        {/* Right Part of N - Plays with a delay */}
                        <path
                            d="M273.53 90.49c-5-7.37-10.76-14.26-17.14-20.44-6-5.81-13.93-9.02-22.33-9.02s-16.67 3.35-22.71 9.35l-28.8 28.89c-1.84 1.79-1.89 4.86-.09 6.7l7.98 8.26c.9.9 2.08 1.46 3.4 1.46 1.32-.05 2.5-.47 3.4-1.37l28.99-29.08c4.25-4.25 11.19-4.25 15.53-.05 5.33 5.19 10.15 10.95 14.35 17.14 12.37 18.27 18.93 39.66 18.93 61.8s-6.52 43.58-18.93 61.85c-4.2 6.18-9.06 11.99-14.45 17.23-4.3 4.15-11.19 4.11-15.44-.14L106.63 123.48c-1.94-1.94-4.58-3.02-7.27-2.88-2.64.14-5.1 1.27-6.85 3.26-9.44 11-11.85 30.45-11.85 40.27 0 22.05 8.83 38.86 13.22 45.09.85 1.13 1.94 2.03 3.35 2.22 1.42.19 2.79-.19 3.87-1.09l8.36-8.45c1.7-1.46 2.17-3.87 1.09-5.85-.71-1.32-4.25-6.94-6.61-15.49-1.27-4.67-2.22-10.2-2.17-16.38.09-7.27 1.46-13.46 1.61-14.16l107.97 107.97c6.04 6.04 14.07 9.35 22.66 9.35s16.38-3.21 22.33-9.02c6.37-6.23 12.18-13.12 17.14-20.49 14.78-21.76 22.57-47.21 22.57-73.65s-7.79-51.89-22.57-73.65l.05-.05Z"
                            style={{
                                strokeDasharray: 1600,
                                strokeDashoffset: 1600,
                                animation: 'drawLine 1.5s ease-in-out forwards',
                                animationDelay: '0.15s'
                            }}
                        />
                    </g>
                </g>
            </svg>
        </div>
    );
};

export default NetworkIconLoader;
